import React, { Component, Fragment } from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import portalCommunication from '@3plearning/mathletics-portal-communication-service';

import css from './student.scss';

import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';

import {
    SELECTED_CLASS,
    SELECTED_DATE,
    SELECTED_STUDENT
} from '../../../components/report-filters/filter-constants';
import {
    getStudentSkillQuestsMasteryData,
    getStudentSkillQuestsMasteryDataCourse,
    resetStudentData,
    setSelectedCourse,
    setSelectedPublication
} from './student-actions';
import modalActions from '../../modal-container/modal-actions';
import FilterHelper from '../../../helpers/filter-helper';
import { getDateRange } from '../../../components/sub-heading/sub-heading-helpers';
import { SKILL_QUESTS_MASTERY_LEGENDS_VARIANTS } from '../skill-quests-mastery-constants';
import { hasValidCourses } from './student-helpers';
import { PORTAL_MODULE } from '../../../helpers/constants';
import { getConfiguration } from '../../../helpers/utils';

import TopLevelInfo from './components/top-level-info/top-level-info';
import TopLevelFilter from './components/top-level-filter/top-level-filter';
import SubHeadingButtons from '../../../components/sub-heading/sub-heading-buttons/sub-heading-buttons';
import exportReportActions from '../../../components/sub-heading/export-report/export-report-actions';
import SkillQuestsMasteryLegends from '../../../components/skill-quests-mastery-legends/skill-quests-mastery-legends';
import ProgressDisplay from './components/progress-display/progress-display';
import TableDisplay from './components/table-display/table-display';
import AssignmentResult from '../../../components/assignment-result/assignment-result';

export class Student extends Component {
    static propTypes = {
        academicYear: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        activeFilters: activeFiltersPropType,
        downloadStudentReport: PropTypes.func,
        exportReport: PropTypes.func,
        exportedReport: PropTypes.shape({
            key: PropTypes.string,
            resourcesMethod: PropTypes.string
        }),
        getStudentSkillQuestsMasteryData: PropTypes.func,
        getStudentSkillQuestsMasteryDataCourse: PropTypes.func,
        history: PropTypes.any,
        isAdmin: PropTypes.bool,
        isLoading: PropTypes.bool,
        isProcessingDownload: PropTypes.bool,
        openModal: PropTypes.func,
        resetStudentData: PropTypes.func,
        selectedCourse: PropTypes.object,
        selectedPublication: PropTypes.object,
        setSelectedCourse: PropTypes.func,
        setSelectedPublication: PropTypes.func,
        skillQuestsMasteryStudentData: PropTypes.object,
        assignmentResultType: PropTypes.number
    };

    constructor(props) {
        super(props);
        this.state = {
            newAssignmentResultType: props.assignmentResultType
        };
    }

    componentDidMount() {
        this.manageInitialData(this.props);
    }

    componentWillUnmount() {
        this.props.resetStudentData();
    }

    componentWillReceiveProps(newProps) {
        this.manageInitialData(newProps);
        this.manageReloadData(this.props, newProps);
        this.manageExportedReport(this.props, newProps);
    }

    /**
     * DATA MANAGEMENT
     */
    manageInitialData = (newProps) => {
        const {
            activeFilters: newActiveFilters,
            skillQuestsMasteryStudentData,
            isLoading,
            assignmentResultType
        } = newProps;

        const shouldLoadStudent =
            !isLoading &&
            !skillQuestsMasteryStudentData &&
            FilterHelper.shouldLoadStudent(newActiveFilters);

        if (shouldLoadStudent) {
            this.props.getStudentSkillQuestsMasteryData(
                newActiveFilters[SELECTED_CLASS].id,
                newActiveFilters[SELECTED_STUDENT].id,
                newActiveFilters[SELECTED_DATE],
                assignmentResultType
            );
        }
    };

    manageReloadData = (props, newProps) => {
        const { activeFilters, assignmentResultType } = this.props;
        const {
            activeFilters: newActiveFilters,
            selectedCourse: newSelectedCourse,
            skillQuestsMasteryStudentData: newSkillQuestsMasteryStudentData,
            isLoading: newIsLoading,
            isFiltered: newIsFiltered,
            assignmentResultType: newAssignmentResultType
        } = newProps;

        this.setState({ newAssignmentResultType });

        const shouldReloadStudent =
            (!newIsLoading &&
                newSkillQuestsMasteryStudentData &&
                FilterHelper.shouldReloadStudent(
                    activeFilters,
                    newActiveFilters
                )) ||
            assignmentResultType !== newAssignmentResultType;

        if (shouldReloadStudent) {
            const {
                selectedCourseId,
                selectedCourseVersion
            } = this.getCourseValues(newSelectedCourse, newIsFiltered);

            this.props.getStudentSkillQuestsMasteryDataCourse(
                newActiveFilters[SELECTED_CLASS].id,
                newActiveFilters[SELECTED_STUDENT].id,
                newActiveFilters[SELECTED_DATE],
                selectedCourseId,
                selectedCourseVersion,
                newAssignmentResultType
            );
        }
    };

    manageExportedReport = (props, newProps) => {
        const { exportedReport } = props;
        const { exportedReport: newExportedReport } = newProps;

        if (exportedReport !== newExportedReport) {
            window.open(newExportedReport.downloadUrl, '_blank');
        }
    };

    getCourseValues = (newSelectedCourse, newIsFiltered) => {
        let selectedCourseId = null;
        let selectedCourseVersion = null;

        if (newSelectedCourse && newIsFiltered) {
            selectedCourseId = newSelectedCourse.selected.id;
            selectedCourseVersion = newSelectedCourse.selected.version;
        }

        return {
            selectedCourseId,
            selectedCourseVersion
        };
    };

    /**
     * HELPERS
     */
    getDateLabel = () => {
        const { academicYear, activeFilters } = this.props;

        return getDateRange(activeFilters[SELECTED_DATE], academicYear);
    };

    onCompletedTextClickHandler = async () => {
        const { activeFilters } = this.props;
        const classId = activeFilters[SELECTED_CLASS].id;

        const moduleId = await getConfiguration(PORTAL_MODULE.ASSIGN_REVIEW);
        portalCommunication.requestModuleChange(moduleId, {
            classId
        });
    };

    render() {
        const {
            academicYear,
            activeFilters,
            assignmentResultType,
            downloadStudentReport,
            exportReport,
            history,
            isAdmin,
            isLoading,
            isProcessingDownload,
            openModal,
            selectedCourse,
            selectedPublication,
            skillQuestsMasteryStudentData
        } = this.props;

        const { STUDENT_ACCURACY } = SKILL_QUESTS_MASTERY_LEGENDS_VARIANTS;

        return (
            <div className={css.component}>
                <div className={css.header}>
                    <TopLevelInfo
                        academicYear={academicYear}
                        activeFilters={activeFilters}
                        history={history}
                        isAdmin={isAdmin}
                        getDateLabel={this.getDateLabel}
                    />
                    <AssignmentResult />
                    <TopLevelFilter
                        activeFilters={activeFilters}
                        skillQuestsMasteryStudentData={
                            skillQuestsMasteryStudentData
                        }
                        assignmentResultType={assignmentResultType}
                        selectedCourse={selectedCourse}
                        selectedPublication={selectedPublication}
                        setSelectedPublication={
                            this.props.setSelectedPublication
                        }
                        setSelectedCourse={this.props.setSelectedCourse}
                        getStudentSkillQuestsMasteryDataCourse={
                            this.props.getStudentSkillQuestsMasteryDataCourse
                        }
                    />
                    <SubHeadingButtons
                        isDownloadEnabled={false}
                        isExportEnabled
                        isProcessingDownload={isProcessingDownload}
                        onExportReport={exportReport}
                        onDownloadReport={downloadStudentReport}
                    />
                </div>
                {hasValidCourses(skillQuestsMasteryStudentData) && (
                    <Fragment>
                        <ProgressDisplay
                            getDateLabel={this.getDateLabel}
                            openModal={openModal}
                            skillQuestsMasteryStudentData={
                                skillQuestsMasteryStudentData
                            }
                            selectedCourse={selectedCourse}
                        />
                        <div className={css['sub-header']}>
                            <SkillQuestsMasteryLegends
                                variant={STUDENT_ACCURACY}
                            />
                        </div>
                    </Fragment>
                )}
                <TableDisplay
                    activeFilters={activeFilters}
                    isLoading={isLoading}
                    skillQuestsMasteryStudentData={
                        skillQuestsMasteryStudentData
                    }
                    onCompletedTextClickHandler={
                        this.onCompletedTextClickHandler
                    }
                    assignmentResultType={this.state.newAssignmentResultType}
                />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        academicYear: state.academicYear,
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        exportedReport: state.exportedReport,
        isAdmin: state.isAdmin,
        isLoading: state.skillQuestsMasteryStudent.isLoading,
        isFiltered: state.skillQuestsMasteryStudent.isFiltered,
        isProcessingDownload: state.downloadReport.isProcessingDownload,
        selectedCourse: state.skillQuestsMasteryStudent.selectedCourse,
        selectedPublication:
            state.skillQuestsMasteryStudent.selectedPublication,
        skillQuestsMasteryStudentData: state.skillQuestsMasteryStudent.data,
        assignmentResultType: state.assignmentResultType
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            downloadStudentReport: exportReportActions.downloadStudentReport,
            exportReport: exportReportActions.exportReport,
            getStudentSkillQuestsMasteryData,
            getStudentSkillQuestsMasteryDataCourse,
            openModal: modalActions.openModal,
            resetStudentData,
            setSelectedCourse,
            setSelectedPublication
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(Student));



// WEBPACK FOOTER //
// ./containers/skill-quests-mastery/student/student.js